import { useState, useCallback } from "react";
import { useAuth } from "../AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "";

const MAX_FILE_SIZE_BYTES = { image: 3 * 1024 * 1024, file: 3 * 1024 * 1024 };

function checkFileSizes(evidenceItems) {
  for (const e of evidenceItems) {
    // Video evidence disabled for now — only file-backed items exist.
    if (/* e.type === "video" || */ !e.file) continue;
    const limit = MAX_FILE_SIZE_BYTES[e.type];
    if (limit && e.file.size > limit) {
      const mb = Math.round(limit / 1024 / 1024);
      throw new Error(`"${e.file.name}" exceeds the ${mb} MB limit for ${e.type}s.`);
    }
  }
}

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export function useAI() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState({});
  const [providers, setProviders] = useState([]);
  const { token } = useAuth();

  const fetchProviders = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/ai/providers`, {
        headers: { ...(token ? { "Authorization": `Bearer ${token}` } : {}) }
      });
      const data = await res.json();
      setProviders(data.providers || []);
    } catch {
      setProviders([]);
    }
  }, [token]);

  const query = useCallback(async ({ provider, model, prompt, subject, maxTokens, evidence = [], queryId, isJudgement = false, parentId, subparentId, disputeCategoryId, jurisdictionState, jurisdictionRegion, desiredOutcome }) => {
    if (!token && !parentId && !subparentId) return;
    
    const id = `${provider}-${Date.now()}`;
    setLoading((prev) => ({ ...prev, [provider]: true }));

    try {
      checkFileSizes(evidence);
      // Process evidence: convert Files to base64
      const processedEvidence = await Promise.all(
        evidence.map(async (e) => {
          // Video evidence disabled for now.
          // if (e.type === 'video') {
          //   return { type: 'video', url: e.url };
          // }
          if (e.file) {
            return {
              type: e.type, // 'image' or 'file'
              mimeType: e.file.type,
              data: await fileToBase64(e.file),
            };
          }
          return null;
        })
      );

      const res = await fetch(`${API_BASE}/api/ai/query`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          provider,
          model,
          prompt,
          subject,
          maxTokens,
          evidence: processedEvidence.filter(e => e !== null),
          queryId,
          isJudgement,
          parentId,
          subparentId,
          disputeCategoryId,
          jurisdictionState,
          jurisdictionRegion,
          desiredOutcome,
        }),
      });
      const data = await res.json();
      const entry = {
        id, 
        resultId: data.resultId, // Actual DB ID
        provider, 
        model: model || "default", 
        prompt,
        subject,
        text: data.text || "", 
        latencyMs: data.latencyMs,
        error: data.error || null, 
        timestamp: new Date().toISOString(),
        isJudgement,
        queryId: data.queryId,
        uuid: data.uuid,
        evidence: evidence.map(e => ({ type: e.type, name: e.file ? e.file.name : e.url }))
      };
      setResults((prev) => [entry, ...prev]);
      return entry;
    } catch (err) {
      const entry = {
        id, provider, model, prompt, subject, text: "", error: err.message,
        timestamp: new Date().toISOString(), isJudgement,
        evidence: evidence.map(e => ({ type: e.type, name: e.file ? e.file.name : e.url }))
      };
      setResults((prev) => [entry, ...prev]);
      return entry;
    } finally {
      setLoading((prev) => ({ ...prev, [provider]: false }));
    }
  }, [token]);

  // Admin-configured model config → { models: { provider: [...] },
  // defaults: { provider: modelId } }. Returns null on failure so callers can
  // fall back to the bundled defaults.
  const fetchProviderModels = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/ai/provider-models`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      return data.models ? { models: data.models, defaults: data.defaults || {} } : null;
    } catch {
      return null;
    }
  }, [token]);

  // Platform-wide general judge (provider + model) configured by the admin.
  // Falls back to Gemini if the request fails or nothing is configured.
  const fetchJudgeConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/ai/judge-config`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      return { provider: data.provider, model: data.model };
    } catch {
      return { provider: "gemini", model: "gemini-3.1-flash-lite" };
    }
  }, [token]);

  // Create a dispute as a draft (status pending_payment) WITHOUT running AI.
  // The panel runs server-side after the filing fee is paid. Returns
  // { queryId, uuid, dispute_status, filing_fee_amount } or { error }.
  const createDraftDispute = useCallback(
    async ({ subject, prompt, desiredOutcome, disputeCategoryId, jurisdictionState, jurisdictionRegion, evidence = [], runConfig, isPublic = false }) => {
      if (!token) return { error: "Not authenticated" };
      try {
        checkFileSizes(evidence);
        const processedEvidence = await Promise.all(
          evidence.map(async (e) => {
            // Video evidence disabled for now.
            // if (e.type === "video") return { type: "video", url: e.url };
            if (e.file) return { type: e.type, mimeType: e.file.type, data: await fileToBase64(e.file), info: e.info?.trim() || null };
            return null;
          })
        );
        const res = await fetch(`${API_BASE}/api/ai/disputes`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            subject,
            prompt,
            desiredOutcome,
            disputeCategoryId,
            jurisdictionState,
            jurisdictionRegion,
            evidence: processedEvidence.filter((e) => e !== null),
            runConfig,
            isPublic,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: data.error || `HTTP ${res.status}` };
        return data;
      } catch (err) {
        return { error: err.message };
      }
    },
    [token]
  );

  // Create/return a PaymentIntent for the filing fee (+ optional stake, in cents)
  // → { clientSecret, fee, stake, amount }.
  const payDisputeFee = useCallback(async (uuid, stakeAmount = 0) => {
    if (!token) return { error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/stripe/disputes/${uuid}/pay-fee`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stakeAmount }),
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  // File a zero-fee (solo Neutral) dispute — no payment, same fulfillment.
  const fileFreeDispute = useCallback(async (uuid) => {
    if (!token) return { error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/stripe/disputes/${uuid}/file-free`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  // Disputant matches the stake → { clientSecret, amount }. Public (token optional).
  const matchStake = useCallback(async (uuid) => {
    try {
      const res = await fetch(`${API_BASE}/api/stripe/disputes/${uuid}/match-stake`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  const finalizeStakeMatch = useCallback(async (uuid) => {
    try {
      const res = await fetch(`${API_BASE}/api/stripe/disputes/${uuid}/match-finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  // After confirming the card, verify + fulfill server-side → { status, judge_at }.
  const finalizeDisputePayment = useCallback(async (uuid) => {
    if (!token) return { error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/stripe/disputes/${uuid}/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  // Add a response/witness turn to a live dispute (no AI runs — the scheduled
  // panel evaluates the full record). Public: works with or without a token.
  const addDisputeTurn = useCallback(async (uuid, { prompt, evidence = [], authorRole, authorEmail, desiredOutcome }) => {
    try {
      checkFileSizes(evidence);
      const processedEvidence = await Promise.all(
        evidence.map(async (e) => {
          // Video evidence disabled for now.
          // if (e.type === "video") return { type: "video", url: e.url };
          if (e.file) return { type: e.type, mimeType: e.file.type, data: await fileToBase64(e.file), info: e.info?.trim() || null };
          return null;
        })
      );
      const res = await fetch(`${API_BASE}/api/ai/disputes/${uuid}/turns`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          prompt,
          desiredOutcome,
          authorRole,
          authorEmail,
          evidence: processedEvidence.filter((e) => e !== null),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { error: data.error || `HTTP ${res.status}` };
      return data;
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  // ── Connect payouts ──
  // Both take the dispute being claimed so the server can onboard/check the
  // account in THAT dispute's Stripe mode — a live case must use a live Connect
  // account even if the admin switch currently sits on test.
  const connectOnboard = useCallback(async (disputeUuid) => {
    if (!token) return { error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/stripe/connect/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ disputeUuid: disputeUuid || null }),
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  const connectStatus = useCallback(async (disputeUuid) => {
    if (!token) return { onboarded: false, payoutsEnabled: false };
    try {
      const qs = disputeUuid ? `?disputeUuid=${encodeURIComponent(disputeUuid)}` : "";
      const res = await fetch(`${API_BASE}/api/stripe/connect/status${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch {
      return { onboarded: false, payoutsEnabled: false };
    }
  }, [token]);

  const claimPayout = useCallback(async (uuid) => {
    if (!token) return { error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/stripe/disputes/${uuid}/claim-payout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }, [token]);

  const fetchSharedDispute = useCallback(async (uuid) => {
    try {
      const res = await fetch(`${API_BASE}/api/share/${uuid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { error: data.error || `HTTP ${res.status}`, status: res.status };
      }
      return data;
    } catch (err) {
      console.error("Failed to fetch shared dispute", err);
      return { error: err.message };
    }
  }, [token]);

  const updateDisputantEmail = useCallback(async (queryId, email) => {
    if (!token) return { ok: false, error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/ai/query/${queryId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ disputant_email: email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      return { ok: true };
    } catch (err) {
      console.error("Failed to update disputant email", err);
      return { ok: false, error: err.message };
    }
  }, [token]);

  // Filer-only: publish (or unpublish) the dispute's final decision page.
  // Returns the resolved visibility so the caller can reflect an admin override.
  const updateDisputeVisibility = useCallback(async (queryId, isPublic) => {
    if (!token) return { ok: false, error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/ai/query/${queryId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ is_public: !!isPublic }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      return { ok: true, ...data };
    } catch (err) {
      console.error("Failed to update dispute visibility", err);
      return { ok: false, error: err.message };
    }
  }, [token]);

  const judgeQuery = useCallback(async ({ judgeProvider, judgeModel, originalPrompt, subject, answers, maxTokens, evidence = [], parentId, subparentId }) => {
    const id = `judge-${judgeProvider}-${Date.now()}`;
    setLoading((prev) => ({ ...prev, [`judge-${judgeProvider}`]: true }));

    const answersBlock = answers
      .map((a, i) => `--- Answer ${i + 1} (${a.provider} / ${a.model}) ---\n${a.text}`)
      .join("\n\n");

    const judgePrompt = `You are an impartial AI judge. Your task is to evaluate the following answers provided by different AI models in response to a user prompt (and any attached evidence), then declare a winner and explain your reasoning.

## Subject
${subject || "No subject provided"}

## Original User Prompt
${originalPrompt}

## Answers to Evaluate
${answersBlock}

## Your Task
1. Carefully read all answers above.
2. Evaluate each answer on: accuracy, completeness, clarity, and usefulness.
3. Declare one winner (use the exact label, e.g. \"Answer 1 (anthropic / claude-sonnet-4-20250514)\").
4. Give a concise but thorough argument for why that answer wins.
5. Give a brief critique of each losing answer explaining what it lacked.

## Response Format

WINNER: [Answer N — provider / model]

WHY IT WINS:
[Your argument here]

CRITIQUE OF OTHER ANSWERS:
[Brief critique of each non-winning answer]

## Structured Output (REQUIRED)

At the very end of your response, also output a fenced JSON block with EXACTLY this shape and nothing else inside the fence:

\`\`\`json
{
  "winner_index": <integer, 1-indexed matching the WINNER above>,
  "confidence": <number between 0.0 and 1.0>,
  "reasons": [<short rationale strings, 2–4 items>]
}
\`\`\``;

    try {
      // Process evidence: convert Files to base64
      const processedEvidence = await Promise.all(
        evidence
          .filter(e => e.file)
          .map(async (e) => ({
            mimeType: e.file.type,
            data: await fileToBase64(e.file),
          }))
      );

      const res = await fetch(`${API_BASE}/api/ai/query`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ 
          provider: judgeProvider, 
          model: judgeModel, 
          prompt: judgePrompt, 
          subject,
          maxTokens: maxTokens || 1024,
          evidence: processedEvidence,
          isJudgement: true,
          queryId: answers[0]?.queryId, // Associate with the same query
          answerIds: answers.map(a => a.resultId), // Pass DB IDs for winner mapping
          parentId,
          subparentId
        }),
      });
      const data = await res.json();
      const entry = {
        id, 
        resultId: data.resultId,
        provider: judgeProvider, 
        model: judgeModel || "default",
        prompt: judgePrompt, 
        subject,
        text: data.text || "", 
        latencyMs: data.latencyMs,
        error: data.error || null, 
        timestamp: new Date().toISOString(),
        isJudgement: true,
        judgedAnswers: answers.map((a) => `${a.provider} / ${a.model}`),
        queryId: data.queryId,
        uuid: data.uuid,
        evidence: evidence.map(e => ({ type: e.type, name: e.file ? e.file.name : e.url }))
      };
      setResults((prev) => [entry, ...prev]);
      return entry;
    } catch (err) {
      const entry = {
        id, provider: judgeProvider, model: judgeModel,
        prompt: judgePrompt, subject, text: "", error: err.message,
        timestamp: new Date().toISOString(), isJudgement: true,
        evidence: evidence.map(e => ({ type: e.type, name: e.file ? e.file.name : e.url }))
      };
      setResults((prev) => [entry, ...prev]);
      return entry;
    } finally {
      setLoading((prev) => ({ ...prev, [`judge-${judgeProvider}`]: false }));
    }
  }, [token]);

  const saveParticipants = useCallback(async (queryId, participants, { sendEmail = true } = {}) => {
    if (!token) return { ok: false, error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/ai/query/${queryId}/participants`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ participants, sendEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      return { ok: true, results: data.results || [] };
    } catch (err) {
      console.error("Failed to save participants", err);
      return { ok: false, error: err.message };
    }
  }, [token]);

  const deleteParticipant = useCallback(async (queryId, participantId) => {
    if (!token) return { ok: false, error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/ai/query/${queryId}/participants/${participantId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      return { ok: true };
    } catch (err) {
      console.error("Failed to delete participant", err);
      return { ok: false, error: err.message };
    }
  }, [token]);

  // kind: "process" (submit-dispute page) or "outcome" (verdict page).
  const submitFeedback = useCallback(async (kind, text) => {
    if (!token) return { ok: false, error: "Not authenticated" };
    try {
      const res = await fetch(`${API_BASE}/api/ai/feedback/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      return { ok: true };
    } catch (err) {
      console.error("Failed to save feedback", err);
      return { ok: false, error: err.message };
    }
  }, [token]);

  const clearSession = useCallback(async (queryId) => {
    try {
      await fetch(`${API_BASE}/api/ai/query/${queryId}/session`, {
        method: "DELETE",
        headers: { ...(token ? { "Authorization": `Bearer ${token}` } : {}) }
      });
    } catch (err) {
      console.error("Failed to clear session data", err);
    }
  }, [token]);

  const sendNotification = useCallback(async (queryId, { force = false } = {}) => {
    try {
      const qs = force ? "?force=1" : "";
      const res = await fetch(`${API_BASE}/api/ai/query/${queryId}/notify${qs}`, {
        method: "POST",
        headers: { ...(token ? { "Authorization": `Bearer ${token}` } : {}) }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      return { ok: true, deduped: !!data.deduped };
    } catch (err) {
      console.error("Failed to send notification", err);
      return { ok: false, error: err.message };
    }
  }, [token]);

  const clearResults = useCallback(() => setResults([]), []);

  return { results, loading, providers, fetchProviders, fetchProviderModels, fetchJudgeConfig, createDraftDispute, addDisputeTurn, payDisputeFee, fileFreeDispute, finalizeDisputePayment, matchStake, finalizeStakeMatch, connectOnboard, connectStatus, claimPayout, query, fetchSharedDispute, judgeQuery, clearResults, updateDisputantEmail, updateDisputeVisibility, clearSession, sendNotification, saveParticipants, deleteParticipant, submitFeedback };
}
